import { PCC_RELEASE_HEADLESS_PDP_FLAG } from '@app/constants/featureFlags';
import { getPartName } from '@app/utils/commonUtils';
import { AlloyButton, InlineLoader, StickySection } from 'cat-ecommerce-alloy';
import cx from 'classnames';
import PropTypes from 'prop-types';
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  FITMENT_VARIANTS,
  PRODUCT_VARIANTS,
  SCENE7_PRESETS,
  STATUS
} from '../../../../constants/commonConstants';
import useAnalytics from '../../../../hooks/useAnalytics';
import Conditional from '../../../common/Conditional';
import ProductCardThumbnail from '../../../common/product/ProductCard/ProductCardThumbnail';
import ProductLink from '../../../common/product/ProductLink';
import ProductOverviewAvailability from '../../../common/product/ProductOverview/ProductOverviewAvailability';
import ProductOverviewPriceDetails from '../../../common/product/ProductOverview/ProductOverviewPriceDetails';
import ProductPrimaryActions from '../../../common/product/ProductPrimaryActions';
import ProductFitment from '../../../common/ProductFitment';
import { COMPARISON_TABLE_ID } from '../constants';
import { getProductURL } from '../ProductDisplayPage/utils';
import pdpStyles from '../styles.module.scss';
import styles from './ProductComparisonTable.module.scss';
import {
  filterAssociations,
  getSpecs,
  isReplaced,
  shouldShowBrand,
  sortByAssociationType
} from './utils';
import { convertFormattedNumberToUnformatted } from '@app/components/pages/checkout/utils';

const PLACEHOLDER = '-';

const ProductComparisonTable = ({
  productId,
  unitOfMeasure,
  didFitmentCallResolve,
  getFitmentResult,
  getFitmentCheck,
  priceCallStatus
}) => {
  const { fireProductImpressionEvent } = useAnalytics();
  const [t] = useTranslation();
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const registrationPendingApproval = useSelector(
    s => s.common?.registrationPendingApproval
  );
  const currency = useSelector(s => s.dealerCustomer?.currency);
  const formattedZeroPrice = useSelector(
    s => s.dealerCustomer?.formattedZeroPrice
  );
  const taxDisclaimerMessage = useSelector(s => s.orders?.taxDisclaimerMessage);
  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );
  const currentProduct = useSelector(s => s.products.byId?.[productId]);
  const priceAndAvailability = useSelector(
    s => s.products.priceAndAvailability
  );
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );
  const esiteURL = useSelector(s => s.common?.esiteURL);
  const isPDPHeadlessFeatureFlagEnable =
    useSelector(s => s.featureFlag?.[PCC_RELEASE_HEADLESS_PDP_FLAG]) ?? false;

  const isPriceLoading = priceCallStatus === STATUS.PENDING;
  const isPriceResolved =
    priceCallStatus === STATUS.RESOLVED || priceCallStatus === STATUS.REJECTED;
  const merchandisingAssociations = (
    currentProduct.merchandisingAssociations ?? []
  )
    ?.filter(assoc => filterAssociations(assoc, priceAndAvailability))
    ?.sort(sortByAssociationType);

  const store = useSelector(s => s.store) ?? '';

  const productsToCompare = useMemo(
    () => [currentProduct, ...merchandisingAssociations],
    [currentProduct, merchandisingAssociations]
  );
  const productsToCompareLength = productsToCompare.length;
  const shouldRender = productsToCompare.length > 1;
  const showCompatibility =
    currentProduct.isFilterByEquipmentEnabled && serialNumber;
  const showBrand = shouldShowBrand(productsToCompare);
  const [spec1, spec2, spec3, spec4] = getSpecs(
    productsToCompare,
    unitOfMeasure
  );
  const [showMore, setShowMore] = useState(false);
  const [displayViewMoreButton, setDisplayViewMoreButton] = useState(false);
  const [elementRefs, setElementRefs] = useState([]);

  const ALTERNATE_PARTS_LIST = 'PCC PDP Page Alternate Parts';
  useEffect(() => {
    const descriptionElements = Array(productsToCompareLength).fill(
      createRef()
    ); // creating an array for all of the description DOM elements

    setElementRefs(descriptionElements);
  }, [productsToCompareLength]);

  useEffect(() => {
    const isTextDescriptionTruncated = () => {
      setDisplayViewMoreButton(false);
      elementRefs.forEach(elementRef => {
        if (
          elementRef?.current?.scrollHeight > elementRef?.current?.clientHeight
        ) {
          setDisplayViewMoreButton(true);
        }
      });
    };
    window.addEventListener('resize', isTextDescriptionTruncated);
    isTextDescriptionTruncated();
    return () =>
      window.removeEventListener('resize', isTextDescriptionTruncated);
  }, [elementRefs]);

  const impressionFired = useRef(false);

  const getOtherStores = store.otherStores?.dealerStores.map(
    store => store.name
  );

  const getProductDetails = useCallback(
    (part, index = 0) => ({
      availability: priceAndAvailability?.[part.partNumber]?.availability,
      brand: part.manufacturer,
      businessUnit: 'PCC',
      category: part.categoryPath?.split('/'),
      checkFitment: getFitmentCheck(part.partNumber),
      list: ALTERNATE_PARTS_LIST,
      itemCatEntryId: priceAndAvailability?.[part.partNumber]?.itemCatEntryId,
      name: getPartName(part.partNumber, part.name),
      partNumber: priceAndAvailability?.[part.partNumber]?.partNumber,
      locationId: getOtherStores?.join('|'),
      shortDescription: part.shortDescription,
      price: convertFormattedNumberToUnformatted(
        priceAndAvailability?.[part.partNumber]?.unformattedUnitPrice || ''
      ),
      discountedAmount: convertFormattedNumberToUnformatted(
        priceAndAvailability?.[part.partNumber]?.discountedAmount || ''
      ),
      productWeight: priceAndAvailability?.[part.partNumber]?.weight,
      position: index + 1,
      quantity: priceAndAvailability?.[part.partNumber]?.quantity,
      relatedModel: model,
      sourcingDetails: priceAndAvailability?.[part.partNumber]?.sourcingDetails
    }),
    [priceAndAvailability, getOtherStores, model]
  );

  const shouldFireImpressionEvent =
    !impressionFired.current &&
    shouldRender &&
    (isCatCorp || registrationPendingApproval || isPriceResolved) &&
    (didFitmentCallResolve || !serialNumber);
  useEffect(() => {
    if (shouldFireImpressionEvent) {
      const productDetails = productsToCompare.map((part, index) =>
        getProductDetails(part, index)
      );

      impressionFired.current = true;
      fireProductImpressionEvent(ALTERNATE_PARTS_LIST, productDetails);
    }
  }, [
    fireProductImpressionEvent,
    getProductDetails,
    productsToCompare,
    shouldFireImpressionEvent
  ]);

  const desktopRowLabelsStyles = 'd-none d-lg-table-cell';
  const desktopSpecsStyles = cx(
    styles['striped'],
    { 'd-none': !showMore },
    'd-lg-table-row'
  );

  const handleShowMore = () => {
    setShowMore(currentShowMore => !currentShowMore);
  };

  const shouldShowPrice = (itemPurchaseDetails = {}) => {
    const isReplacedPart = isReplaced(itemPurchaseDetails);
    const isZeroPrice =
      itemPurchaseDetails?.discountedPrice === formattedZeroPrice;
    return (
      itemPurchaseDetails.enableAddToCart &&
      itemPurchaseDetails.discountedPrice &&
      !isZeroPrice &&
      !isReplacedPart
    );
  };

  const isThisItem = index => index === 0;
  const getCellClass = index => (isThisItem(index) ? styles['primary'] : '');
  const urlBasedonFeatureFlag = (partNumber, productURL) =>
    isPDPHeadlessFeatureFlagEnable
      ? `${esiteURL}/product/${partNumber}`
      : getProductURL(productURL);

  const getProductImage = (p = {}, index = 0) => (
    <td
      key={`cap__productImage${p.partNumber}`}
      className={cx(getCellClass(index), 'text-center pb-0')}
    >
      <ProductCardThumbnail
        alt={p.name}
        className="d-inline-block"
        height="200px"
        imageUrl={p?.imageURL || p?.partsImages?.[0]?.url}
        preset={SCENE7_PRESETS.E3}
        url={urlBasedonFeatureFlag(p.partNumber, p.productURL)}
        width="200px"
        productClickDetails={getProductDetails(p)}
        gaListName={ALTERNATE_PARTS_LIST}
      />
      {isThisItem(index) && (
        <p className="text-start fw-bold mb-0">{t('PDP_THIS_ITEM')}</p>
      )}
    </td>
  );

  const getProductName = (p = {}, index = 0) => (
    <td
      key={`cap__productName${p.partNumber}`}
      className={cx(getCellClass(index), 'py-0 align-top')}
    >
      <ProductLink
        className="text-xl text-univers--bold u-color--ExtraDarkGray"
        link={urlBasedonFeatureFlag(p.partNumber, p.productURL)}
        productClickDetails={getProductDetails(p)}
        gaListName={ALTERNATE_PARTS_LIST}
      >
        {getPartName(p.partNumber, p.name)}
      </ProductLink>
    </td>
  );

  const getPrice = (p = {}, index = 0) => {
    const itemPurchaseDetails = priceAndAvailability?.[p.partNumber] ?? {};
    const showPrice = shouldShowPrice(itemPurchaseDetails);
    return (
      <td key={`cap__price${p.partNumber}`} className={cx(getCellClass(index))}>
        {showPrice ? (
          <ProductOverviewPriceDetails
            currency={currency}
            taxDisclaimerMessage={taxDisclaimerMessage}
            price={itemPurchaseDetails?.discountedPrice}
            unitPrice={itemPurchaseDetails?.unitPrice}
          />
        ) : null}
      </td>
    );
  };
  const getAvailability = (p = {}, index = 0) => {
    const itemPurchaseDetails = priceAndAvailability?.[p.partNumber] ?? {};
    const showAvailability = shouldShowPrice(itemPurchaseDetails);
    return (
      <td
        key={`cap__availability${p.partNumber}`}
        className={getCellClass(index)}
      >
        <Conditional fallback={<InlineLoader />} test={!isPriceLoading}>
          {showAvailability ? (
            <ProductOverviewAvailability
              availability={itemPurchaseDetails.availability}
            />
          ) : null}
        </Conditional>
      </td>
    );
  };
  const getCta = (p = {}, index = 0) => (
    <td key={`cap__cta${p.partNumber}`} className={cx(getCellClass(index))}>
      <ProductPrimaryActions
        priceCallStatus={priceCallStatus}
        productId={p.partNumber}
        quantity={1}
        variant={PRODUCT_VARIANTS.card}
        url={urlBasedonFeatureFlag(p.partNumber, p.productURL)}
        productClickDetails={[getProductDetails(p)]}
        gaListName={ALTERNATE_PARTS_LIST}
        partNumber={p.partNumber}
      />
    </td>
  );
  const getFitment = (p = {}, index = 0) => (
    <td key={`cap__fitment${p.partNumber}`} className={cx(getCellClass(index))}>
      <p className={cx('fw-bold', 'text-xs', 'd-lg-none')}>
        {t('COMPATIBILITY')}
      </p>
      <ProductFitment
        className="my-2"
        partNumber={p.partNumber}
        variant={FITMENT_VARIANTS.small}
        didFitmentCallResolve={didFitmentCallResolve}
        getFitmentResult={getFitmentResult}
      />
    </td>
  );
  const getBrand = (p = {}, index) => (
    <td key={`cap__brand${p.partNumber}`} className={cx(getCellClass(index))}>
      <p className={cx('fw-bold', 'text-xs', 'd-lg-none')}>{t('CPG_BRAND')}</p>
      {p.manufacturer ?? PLACEHOLDER}
    </td>
  );
  const getSpecification = ({ p = {}, spec, index }) => (
    <td
      key={`cap__spec${spec}__${p.partNumber}`}
      className={cx(getCellClass(index))}
    >
      <p className={cx('fw-bold', 'text-xs', 'd-lg-none')}>{spec}</p>
      {p?.attributes?.find(a => a.name === spec)?.value ?? PLACEHOLDER}
    </td>
  );

  const getDescription = (p = {}, index = 0) => (
    <td
      key={`cap__description${p.partNumber}`}
      className={cx(getCellClass(index), {
        [styles['mobile-description-border-bottom']]: !showMore
      })}
    >
      <p className={cx('fw-bold', 'text-xs', 'd-lg-none')}>
        {t('DESCRIPTION')}
      </p>
      <p className="d-none d-lg-block">{p.shortDescription ?? PLACEHOLDER}</p>
      <p
        className={cx('d-lg-none', {
          [styles['cap__description']]: !showMore
        })}
        ref={elementRefs[index]}
      >
        {p.shortDescription ?? PLACEHOLDER}
      </p>
    </td>
  );

  const showMoreButton = (
    scenario2,
    scenario3,
    scenario4,
    shouldDisplayViewMore,
    buttonText
  ) => {
    const showrMoreText = buttonText
      ? t('VIEW_FEWER_DETAILS')
      : t('VIEW_MORE_DETAILS');
    return (
      <>
        {scenario2 || scenario3 || scenario4 || shouldDisplayViewMore ? (
          <StickySection className={cx('d-flex align-items-center start-0')}>
            <AlloyButton
              buttonType="ghost"
              size="large"
              className={cx('d-lg-none', 'px-0')}
              onClick={handleShowMore}
            >
              {showrMoreText}
            </AlloyButton>
          </StickySection>
        ) : null}
      </>
    );
  };

  return shouldRender ? (
    <section className="py-3" id={COMPARISON_TABLE_ID}>
      <h2 className="h3 display-3">{t('COMPARE_ALT_PARTS')}</h2>
      <div className={`${pdpStyles['overflow-x-auto']} w-100 py-1`}>
        <table
          className={cx(
            styles['compare-parts__table'],
            'mb-3',
            'mb-lg-4',
            'mt-4',
            'position-relative'
          )}
        >
          <tbody>
            <tr>
              <th className={desktopRowLabelsStyles} />
              {productsToCompare.map(getProductImage)}
            </tr>
            <tr>
              <th className={desktopRowLabelsStyles} />
              {productsToCompare.map(getProductName)}
            </tr>
            <tr>
              <th className={desktopRowLabelsStyles} />
              {productsToCompare.map(getPrice)}
            </tr>
            <tr>
              <th className={desktopRowLabelsStyles} />
              {productsToCompare.map(getAvailability)}
            </tr>
            <tr>
              <th className={desktopRowLabelsStyles} />
              {productsToCompare.map(getCta)}
            </tr>
            {showCompatibility && (
              <tr className={styles['striped']}>
                <th className={desktopRowLabelsStyles}>{t('COMPATIBILITY')}</th>
                {productsToCompare.map(getFitment)}
              </tr>
            )}
            {showBrand && (
              <tr className={styles['striped']}>
                <th className={desktopRowLabelsStyles}>{t('CPG_BRAND')}</th>
                {productsToCompare.map(getBrand)}
              </tr>
            )}
            {spec1 && (
              <tr className={styles['striped']}>
                <th className={desktopRowLabelsStyles}>{spec1}</th>
                {productsToCompare.map((p, index) =>
                  getSpecification({ p, spec1, index })
                )}
              </tr>
            )}
            <tr className={styles['striped']}>
              <th className={desktopRowLabelsStyles}>{t('DESCRIPTION')}</th>
              {productsToCompare.map(getDescription)}
            </tr>
            {spec2 && (
              <tr className={desktopSpecsStyles}>
                <th className={desktopRowLabelsStyles}>{spec2}</th>
                {productsToCompare.map((p, index) =>
                  getSpecification({ p, spec2, index })
                )}
              </tr>
            )}
            {spec3 && (
              <tr className={desktopSpecsStyles}>
                <th className={desktopRowLabelsStyles}>{spec3}</th>
                {productsToCompare.map((p, index) =>
                  getSpecification({ p, spec3, index })
                )}
              </tr>
            )}
            {spec4 && (
              <tr className={desktopSpecsStyles}>
                <th className={desktopRowLabelsStyles}>{spec4}</th>
                {productsToCompare.map((p, index) =>
                  getSpecification({ p, spec4, index })
                )}
              </tr>
            )}
          </tbody>
        </table>
        {showMoreButton(spec2, spec3, spec4, displayViewMoreButton, showMore)}
      </div>
    </section>
  ) : null;
};

ProductComparisonTable.propTypes = {
  productId: PropTypes.string,
  unitOfMeasure: PropTypes.string.isRequired,
  didFitmentCallResolve: PropTypes.bool,
  getFitmentResult: PropTypes.func,
  getFitmentCheck: PropTypes.func,
  priceCallStatus: PropTypes.string.isRequired
};

export default ProductComparisonTable;
